import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
	DevicePhoneMobileIcon,
	LockClosedIcon,
	CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Modal, OtpInput, Button } from '../ui';
import { otpApi, ApiError } from '../../api';

type VerificationStep = 'confirm' | 'otp' | 'success';

export interface PhoneVerificationModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Phone number from primary location (already masked by backend, e.g., "****6743") */
	phoneNumber?: string;
	/** Callback when verification is successful */
	onVerified?: () => void;
}

const PhoneVerificationModal = ({
	isOpen,
	onClose,
	phoneNumber,
	onVerified,
}: PhoneVerificationModalProps) => {
	const [step, setStep] = useState<VerificationStep>('confirm');
	const [maskedPhone, setMaskedPhone] = useState('');
	const [otp, setOtp] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	// Reset state when modal opens
	useEffect(() => {
		if (isOpen) {
			setStep('confirm');
			setMaskedPhone('');
			setOtp('');
			setError('');
			setIsLoading(false);
		}
	}, [isOpen]);

	const handleSendCode = async () => {
		setIsLoading(true);
		setError('');

		try {
			const response = await otpApi.send();
			if (response.success) {
				// Extract masked phone from message (e.g., "OTP sent successfully to ****4288")
				setMaskedPhone(response.message);
				toast.success(response.message);
				// Move to OTP step
				setStep('otp');
			} else {
				setError('Failed to send verification code. Please try again.');
			}
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to send verification code. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyOtp = async () => {
		if (otp.length !== 6) {
			setError('Please enter the complete 6-digit code');
			return;
		}

		setIsLoading(true);
		setError('');

		try {
			const response = await otpApi.verify(otp);
			if (response.success) {
				// Move to success step
				setStep('success');
				// Notify parent of successful verification
				onVerified?.();
			} else {
				setError('Invalid verification code. Please try again.');
				setOtp('');
			}
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Invalid verification code. Please try again.');
			setOtp('');
		} finally {
			setIsLoading(false);
		}
	};

	const handleOtpComplete = (completedOtp: string) => {
		// Auto-verify when all 6 digits are entered
		if (completedOtp.length === 6 && !isLoading) {
			// Small delay to allow UI to update before verification
			setTimeout(() => handleVerifyOtp(), 100);
		}
	};

	const handleResendCode = async () => {
		setOtp('');
		setError('');
		setIsLoading(true);

		try {
			const response = await otpApi.resend();
			if (response.success) {
				toast.success('Verification code resent successfully!');
			} else {
				setError('Failed to resend code. Please try again.');
			}
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to resend code. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleDone = () => {
		onClose();
	};

	const handleClose = () => {
		// Only allow close if not in loading state
		if (!isLoading) {
			onClose();
		}
	};

	// Custom gradient header
	const headerContent = (
		<div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] px-6 py-5 sm:px-8 sm:py-6">
			<h2 className="text-white text-lg sm:text-xl font-semibold flex items-center gap-3">
				<DevicePhoneMobileIcon className="w-6 h-6" />
				Phone Verification
			</h2>
		</div>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size="sm"
			headerContent={headerContent}
			bodyClassName="text-center"
		>
			{/* Step 1: Confirm to Send Code */}
			{step === 'confirm' && (
				<div className="phone-verification__step">
					<div className="phone-verification__icon phone-verification__icon--purple">
						<DevicePhoneMobileIcon className="w-10 h-10" />
					</div>

					<h3 className="phone-verification__title">Verify Your Phone Number</h3>
					<p className="phone-verification__description">
						We'll send a 6-digit verification code to your registered phone number
						{phoneNumber && <strong> {phoneNumber}</strong>}.
					</p>

					{error && <p className="phone-verification__error mb-4">{error}</p>}

					<Button
						variant="primary"
						size="lg"
						className="w-full"
						onClick={handleSendCode}
						isLoading={isLoading}
						disabled={isLoading}
					>
						Send Verification Code
					</Button>
				</div>
			)}

			{/* Step 2: Enter OTP */}
			{step === 'otp' && (
				<div className="phone-verification__step">
					<div className="phone-verification__icon phone-verification__icon--purple">
						<LockClosedIcon className="w-10 h-10" />
					</div>

					<h3 className="phone-verification__title">Enter Verification Code</h3>
					<p className="phone-verification__description">
						{maskedPhone || 'Enter the 6-digit code sent to your phone'}
					</p>

					<div className="phone-verification__otp-container">
						<OtpInput
							value={otp}
							onChange={setOtp}
							onComplete={handleOtpComplete}
							disabled={isLoading}
							error={!!error}
							autoFocus
						/>
						{error && <p className="phone-verification__error">{error}</p>}
					</div>

					<Button
						variant="primary"
						size="lg"
						className="w-full mb-4"
						onClick={handleVerifyOtp}
						isLoading={isLoading}
						disabled={isLoading || otp.length !== 6}
					>
						Verify Code
					</Button>

					<p className="phone-verification__resend">
						Didn't receive the code?{' '}
						<button
							type="button"
							onClick={handleResendCode}
							disabled={isLoading}
							className="phone-verification__resend-link"
						>
							Resend Code
						</button>
					</p>
				</div>
			)}

			{/* Step 3: Success */}
			{step === 'success' && (
				<div className="phone-verification__step">
					<div className="phone-verification__icon phone-verification__icon--green">
						<CheckCircleIcon className="w-10 h-10" />
					</div>

					<h3 className="phone-verification__title">Phone Verified!</h3>
					<p className="phone-verification__description">
						Your phone number has been successfully verified. You'll now receive SMS
						notifications for appointments.
					</p>

					<Button variant="primary" size="lg" className="w-full" onClick={handleDone}>
						Done
					</Button>
				</div>
			)}
		</Modal>
	);
};

export default PhoneVerificationModal;
